/**
 * KT-193 — restore the grid scroll position on back navigation.
 *
 * Tapping a product on a long collection and pressing Back returned the shopper
 * to the top of the grid, so they had to scroll and re-find their place. On
 * mobile, where a collection is many screens long, that is the difference
 * between browsing and giving up.
 *
 * Native scroll restoration cannot do this on its own here: the grid supports
 * load-more / infinite pagination, so on Back the DOM only holds page 1 and the
 * saved offset is past the end of the document. We re-request the additional
 * pages the shopper had loaded before restoring.
 *
 * Storage: sessionStorage, one entry per collection URL. This does NOT touch
 * the KT-171 / KT-150 exact-SKU redirect guard in main-search.liquid: separate
 * key namespace, and restore only runs on a back_forward navigation while that
 * guard only fires on a fresh push navigation.
 */
(function () {
  'use strict';

  var KEY_PREFIX = 'ktPlpScroll:';
  var MAX_AGE_MS = 30 * 60 * 1000;
  var MAX_AUTO_PAGES = 5;

  // `[data-contentlm-replace]` is the container the theme's load-more swaps, so
  // it is the one that actually grows. `.t4s-products` is the fallback for
  // grids rendered without pagination.
  var grid = document.querySelector('[data-contentlm-replace]') ||
             document.querySelector('.t4s-products');
  if (!grid) return;

  function key() {
    return KEY_PREFIX + location.pathname + location.search;
  }

  function save() {
    var offset = window.scrollY || window.pageYOffset || 0;
    if (offset < 200) {
      // Near the top there is nothing worth restoring, and storing it would
      // override a genuinely useful earlier entry.
      try { sessionStorage.removeItem(key()); } catch (e) { /* private mode */ }
      return;
    }
    try {
      sessionStorage.setItem(key(), JSON.stringify({
        y: offset,
        cards: grid.querySelectorAll('.t4s-product').length,
        at: Date.now()
      }));
    } catch (e) { /* quota / private mode — restoring is optional */ }
  }

  function read() {
    try {
      var raw = sessionStorage.getItem(key());
      if (!raw) return null;
      var entry = JSON.parse(raw);
      if (!entry || typeof entry.y !== 'number') return null;
      if (Date.now() - entry.at > MAX_AGE_MS) return null;
      return entry;
    } catch (e) {
      return null;
    }
  }

  // Save on the way out. `pagehide` covers back/forward cache and real unloads;
  // `visibilitychange` covers iOS Safari, which does not always fire pagehide.
  window.addEventListener('pagehide', save);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') save();
  });
  // A tap on a product card is the case this exists for — record it there and
  // then, before the navigation starts.
  grid.addEventListener('click', function (event) {
    if (event.target.closest('a[href]')) save();
  }, true);

  var navigation = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
  var isBack = navigation ? navigation.type === 'back_forward' : false;
  var entry = isBack ? read() : null;
  if (!entry) return;

  // Stop the browser fighting us with its own (wrong, because the grid is
  // short) restore. Only set on pages this script runs on.
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

  var pagesLoaded = 0;

  function restore() {
    var target = Math.min(entry.y, document.documentElement.scrollHeight - window.innerHeight);
    window.scrollTo({ top: Math.max(target, 0), behavior: 'auto' });
  }

  function step() {
    var shortOfCards = grid.querySelectorAll('.t4s-product').length < entry.cards;
    var loadMore = document.querySelector('[data-load-more]:not([data-is-prev])');

    if (shortOfCards && loadMore && pagesLoaded < MAX_AUTO_PAGES) {
      pagesLoaded += 1;
      loadMore.click();
      // The grid is replaced asynchronously; watch for it rather than guessing
      // a timeout.
      var observer = new MutationObserver(function () {
        observer.disconnect();
        window.setTimeout(step, 0);
      });
      observer.observe(grid, { childList: true, subtree: true });
      window.setTimeout(function () {
        observer.disconnect();
        restore();
      }, 3000);
      return;
    }

    restore();
  }

  if (document.readyState === 'complete') {
    step();
  } else {
    window.addEventListener('load', step, { once: true });
  }
})();
